/**
 * SkyLutSet — owns the four Hillaire LUTs and their dirty-flag regeneration.
 *
 * Two regeneration tiers (Hillaire §3, "what depends on what"):
 *
 *   Tier A — Transmittance + Multi-Scatter
 *     Depend ONLY on the atmosphere config. Re-baked when the config key
 *     changes. Rarely (only when the planet / scattering coefficients
 *     change), so these are the expensive-but-infrequent bakes.
 *
 *   Tier B — Sky-View + Aerial-Perspective
 *     Depend on Tier A PLUS sun direction and camera altitude. Re-baked when
 *     the sun moves beyond `sunCosThreshold` (in cosine of sun-zenith) or the
 *     camera altitude moves beyond `altitudeThresholdKm`. NOT every frame.
 *
 * `update()` is called each frame by SkyRuntimeSystem with the current sun
 * direction + camera altitude; it returns which tiers (if any) re-baked so
 * the caller can trigger a PMREM env re-bake only when the sky actually
 * changed.
 *
 * Aerial-perspective baking is gated behind `enableAerialPerspective` (off by
 * default — its compositing is deferred, see render-sky.md).
 */

import type { AtmosphereConfig } from '../AtmosphereConfig.js';
import { atmosphereConfigKey } from '../AtmosphereConfig.js';
import { Logger } from '../../../core/Logger.js';
import {
  bakeTransmittanceLut, type TransmittanceLutResult,
} from './TransmittanceLut.js';
import {
  bakeMultiScatterLut, type MultiScatterLutResult,
} from './MultiScatterLut.js';
import {
  bakeSkyViewLut, type SkyViewLutResult,
} from './SkyViewLut.js';
import {
  bakeAerialPerspectiveLut, type AerialPerspectiveLutResult, type AerialFrame,
} from './AerialPerspectiveLut.js';

export interface SkyLutSetOptions {
  atmosphere: AtmosphereConfig;
  /** Sun zenith cosine change that triggers a Tier-B re-bake. Default 0.01. */
  sunCosThreshold?: number;
  /** Camera-altitude change (km) that triggers a Tier-B re-bake. Default 0.05. */
  altitudeThresholdKm?: number;
  /**
   * THE AERIAL ALTITUDE (2026-10-06): the aerial volume's own observer-altitude change (km) that re-bakes the VOLUME alone
   * (see `setAerialViewHeightKm`). Default 0.025.
   */
  aerialAltitudeThresholdKm?: number;
  /** Sun illuminance multiplier baked into sky-view radiance. Default 20. */
  sunIlluminance?: number;
  /** Bake the aerial-perspective volume too (compositing deferred). Default false. */
  enableAerialPerspective?: boolean;
  /** The aerial volume's direction frame (AerialPerspectiveLut's AerialFrame WHY). Default 'screen'. */
  aerialFrame?: AerialFrame;
}

export interface SkyLutUpdateResult {
  /** Transmittance + multi-scatter re-baked this update. */
  tierA: boolean;
  /** Sky-view (+ optional AP) re-baked this update — or only the AP volume after a frame switch. */
  tierB: boolean;
}

/**
 * The FULL-precision config key (shader sweep 2026-10-06). `atmosphereConfigKey` rounds to 7 digits for cheap change
 * detection; a forced update compares this one so it can skip a tier-A re-bake whose output would be bit-identical
 * (same config) while still catching an in-place edit below the 7-digit key.
 */
function exactConfigKey(cfg: AtmosphereConfig): string {
  return [
    cfg.bottomRadius, cfg.topRadius,
    cfg.rayleighScattering.x, cfg.rayleighScattering.y, cfg.rayleighScattering.z, cfg.rayleighScaleHeight,
    cfg.mieScattering.x, cfg.mieScattering.y, cfg.mieScattering.z,
    cfg.mieExtinction.x, cfg.mieExtinction.y, cfg.mieExtinction.z, cfg.mieScaleHeight, cfg.miePhaseG,
    cfg.ozoneExtinction.x, cfg.ozoneExtinction.y, cfg.ozoneExtinction.z, cfg.ozoneCenterAltitude, cfg.ozoneHalfWidth,
    cfg.groundAlbedo.x, cfg.groundAlbedo.y, cfg.groundAlbedo.z,
    cfg.mieBackgroundFraction ?? 0, cfg.mieBackgroundScaleHeight ?? 0,
  ].join('|');
}

export class SkyLutSet {
  private _cfg: AtmosphereConfig;
  private _cfgKey: string;
  private readonly _sunCosThreshold: number;
  private readonly _altThreshold: number;
  private _sunIlluminance: number;
  private readonly _enableAP: boolean;
  private _aerialFrame: AerialFrame;

  private _transmittance: TransmittanceLutResult | null = null;
  private _multiScatter: MultiScatterLutResult | null = null;
  private _skyView: SkyViewLutResult | null = null;
  private _aerial: AerialPerspectiveLutResult | null = null;

  // Last Tier-B inputs for threshold checks.
  private _lastSunCos = Number.NaN;
  private _lastAltKm = Number.NaN;
  /** The sun illuminance the current tier-B LUTs were baked with (a forced update re-bakes on a change). */
  private _lastSunIllum = Number.NaN;
  /** The exact config the tier-A LUTs were baked from (`exactConfigKey`). */
  private _lastExactKey = '';
  /** The aerial volume must be re-baked in a new frame (the sky-view does not depend on the frame). */
  private _aerialDirty = false;
  /**
   * THE AERIAL ALTITUDE (2026-10-06): the observer radius (km) the aerial VOLUME is baked from, when it differs from the
   * sky-view's. NaN = follow the sky-view's `viewHeightKm` (the old behaviour, bit for bit). WHY: a consumer that holds the
   * sky-view observer at the ground (`trackCameraAltitude: false` — a hill must not re-bake the sky in visible steps) baked
   * the froxels from 1 m above the planet too, and every ray below the horizon was clipped at that "ground" within metres:
   * transmittance ≈ 1 on all terrain at any distance — no aerial haze on the ground, by day or night (Atlas, measured).
   */
  private _aerialAltKm = Number.NaN;
  private _lastAerialAltKm = Number.NaN;
  private readonly _aerialAltThreshold: number;
  private _disposed = false;

  constructor(opts: SkyLutSetOptions) {
    this._cfg = opts.atmosphere;
    this._cfgKey = atmosphereConfigKey(opts.atmosphere);
    this._sunCosThreshold = opts.sunCosThreshold ?? 0.01;
    this._altThreshold = opts.altitudeThresholdKm ?? 0.05;
    this._aerialAltThreshold = opts.aerialAltitudeThresholdKm ?? 0.025;
    this._sunIlluminance = opts.sunIlluminance ?? 20;
    this._enableAP = opts.enableAerialPerspective ?? false;
    this._aerialFrame = opts.aerialFrame ?? 'screen';
  }

  /** The aerial volume's frame; a change re-bakes tier B on the next update (sky/post 2026-09-27). */
  get aerialFrame(): AerialFrame { return this._aerialFrame; }
  setAerialFrame(frame: AerialFrame): void {
    if (frame === this._aerialFrame) return;
    this._aerialFrame = frame;
    // The next update re-bakes the VOLUME in the new frame. It used to NaN the sun key and re-bake the sky-view too —
    // bit-identical work (the sky-view never reads the frame), ~125 ms of it on the main thread at every boot, where
    // Atlas's post chain flips the engine's 'screen' default to 'world' (shader sweep 2026-10-06).
    this._aerialDirty = true;
  }

  /** The aerial volume's own observer radius (km); null = follow the sky-view's (THE AERIAL ALTITUDE). */
  get aerialViewHeightKm(): number | null { return Number.isFinite(this._aerialAltKm) ? this._aerialAltKm : null; }
  setAerialViewHeightKm(radiusKm: number | null): void {
    this._aerialAltKm = radiusKm !== null && Number.isFinite(radiusKm) ? radiusKm : Number.NaN;
  }

  get transmittance(): TransmittanceLutResult | null { return this._transmittance; }
  get multiScatter(): MultiScatterLutResult | null { return this._multiScatter; }
  get skyView(): SkyViewLutResult | null { return this._skyView; }
  get aerial(): AerialPerspectiveLutResult | null { return this._aerial; }
  get sunIlluminance(): number { return this._sunIlluminance; }
  get aerialPerspectiveEnabled(): boolean { return this._enableAP; }

  setSunIlluminance(v: number): void { this._sunIlluminance = v; }

  /** Push a new atmosphere config; forces a Tier-A (+B) re-bake next update. */
  setAtmosphere(cfg: AtmosphereConfig): void {
    this._cfg = cfg;
    this._cfgKey = atmosphereConfigKey(cfg);
  }

  /**
   * Re-bake whichever tiers are dirty.
   *
   * @param cosSunZenith  current sun-zenith cosine in the camera's up-frame
   * @param viewHeightKm  current camera radius from planet centre (km)
   * @param force         force both tiers (e.g. first frame after teleport)
   */
  update(cosSunZenith: number, viewHeightKm: number, force = false): SkyLutUpdateResult {
    if (this._disposed) return { tierA: false, tierB: false };

    let tierA = false;
    let tierB = false;

    // ── Tier A: config-only LUTs ──
    // Dirty when never baked OR the config key baked-against differs from the
    // current config key (setAtmosphere updates _cfgKey out of band).
    // A FORCED update re-bakes tier A only when the config really differs (exactly) from the one baked: tier A reads
    // nothing but the config, so a forced re-bake of the same config is the same bits. Every env-bake request (a
    // cloudiness bucket, the IBL policy's request / restart / one-shot) arrives here as a force and used to re-bake
    // all four LUTs — ~420 ms of main-thread CPU each on the shipped tiers (shader sweep 2026-10-06).
    const needTierA = !this._transmittance
      || !this._multiScatter
      || this._lastBakedKey !== this._cfgKey
      || (force && exactConfigKey(this._cfg) !== this._lastExactKey);
    if (needTierA) {
      this._bakeTierA();
      tierA = true;
      // Tier A always invalidates Tier B.
      tierB = true;
    }

    // ── Tier B: sun + altitude dependent LUTs ──
    const sunMoved = Number.isNaN(this._lastSunCos)
      || Math.abs(cosSunZenith - this._lastSunCos) > this._sunCosThreshold;
    const altMoved = Number.isNaN(this._lastAltKm)
      || Math.abs(viewHeightKm - this._lastAltKm) > this._altThreshold;
    // A FORCED update whose tier-B inputs are bit-identical to the last bake's (same sun cosine, altitude, illuminance,
    // tier A unchanged) would bake the same bits — skipped. A force with ANY difference, however small, still bakes at
    // the exact inputs (the IBL one-shot samples the LUT at this frame's sun).
    const sameInputs = Object.is(cosSunZenith, this._lastSunCos) && Object.is(viewHeightKm, this._lastAltKm)
      && Object.is(this._sunIlluminance, this._lastSunIllum);
    // (a frame switch after an illuminance change re-bakes both — the sky-view's inputs moved too)
    const fullB = tierB || (force && !sameInputs) || sunMoved || altMoved || !this._skyView
      || (this._aerialDirty && !Object.is(this._sunIlluminance, this._lastSunIllum));
    // THE AERIAL ALTITUDE: the volume's observer — its own when set, else the sky-view's.
    const aerialAlt = Number.isFinite(this._aerialAltKm) ? this._aerialAltKm : viewHeightKm;
    const aerialMoved = this._enableAP && Number.isFinite(this._aerialAltKm)
      && !(Math.abs(aerialAlt - this._lastAerialAltKm) <= this._aerialAltThreshold);
    if (fullB) {
      this._bakeTierB(cosSunZenith, viewHeightKm, aerialAlt);
      this._lastSunCos = cosSunZenith;
      this._lastAltKm = viewHeightKm;
      this._lastSunIllum = this._sunIlluminance;
      this._aerialDirty = false;
      tierB = true;
    } else if (this._aerialDirty || aerialMoved) {
      // A frame switch or an aerial-altitude move alone: the volume, at the sun the sky-view was baked with (a frame
      // switch with no aerial altitude set is identical to a fresh full bake).
      if (this._enableAP && this._transmittance && this._multiScatter) {
        this._bakeAerial(this._lastSunCos, Number.isFinite(this._aerialAltKm) ? aerialAlt : this._lastAltKm);
        tierB = true;
      }
      this._aerialDirty = false;
    }

    return { tierA, tierB };
  }

  /** Config key that the current Tier-A LUTs were baked against. */
  private _lastBakedKey = '';

  private _bakeTierA(): void {
    this._transmittance?.texture.dispose();
    this._multiScatter?.texture.dispose();

    const t = bakeTransmittanceLut(this._cfg);
    this._transmittance = t;
    const ms = bakeMultiScatterLut({
      cfg: this._cfg,
      transmittanceData: t.data,
      transmittanceWidth: t.width,
      transmittanceHeight: t.height,
    });
    this._multiScatter = ms;
    this._lastBakedKey = this._cfgKey;
    this._lastExactKey = exactConfigKey(this._cfg);
    Logger.info('SkyLUT', 'Baked transmittance (256×64) + multi-scatter (32×32)');
  }

  private _bakeTierB(cosSunZenith: number, viewHeightKm: number, aerialViewHeightKm = viewHeightKm): void {
    if (!this._transmittance || !this._multiScatter) { this._bakeTierA(); }
    const tt = this._transmittance!;
    const mss = this._multiScatter!;

    const sv = bakeSkyViewLut({
      cfg: this._cfg,
      viewHeight: viewHeightKm,
      cosSunZenith,
      transmittanceData: tt.data,
      transmittanceWidth: tt.width,
      transmittanceHeight: tt.height,
      multiScatterData: mss.data,
      multiScatterSize: mss.width,
      sunIlluminance: this._sunIlluminance,
    });
    // Reuse the existing GPU texture if dimensions match so the background
    // node's texture reference stays valid across re-bakes (no swap → no
    // material recompile). The bake's freshly-allocated texture is discarded.
    if (this._skyView
      && this._skyView.width === sv.width && this._skyView.height === sv.height) {
      (this._skyView.texture.image.data as Float32Array).set(sv.data);
      this._skyView.texture.needsUpdate = true;
      this._skyView.data = sv.data;
      sv.texture.dispose();
    } else {
      this._skyView?.texture.dispose();
      this._skyView = sv;
    }

    if (this._enableAP) this._bakeAerial(cosSunZenith, aerialViewHeightKm);
  }

  /** The aerial-perspective volume for these inputs (tier A must exist). */
  private _bakeAerial(cosSunZenith: number, viewHeightKm: number): void {
    this._lastAerialAltKm = viewHeightKm;
    const tt = this._transmittance!;
    const mss = this._multiScatter!;
    const ap = bakeAerialPerspectiveLut({
      cfg: this._cfg,
      viewHeight: viewHeightKm,
      cosSunZenith,
      transmittanceData: tt.data,
      transmittanceWidth: tt.width,
      transmittanceHeight: tt.height,
      multiScatterData: mss.data,
      multiScatterSize: mss.width,
      sunIlluminance: this._sunIlluminance,
      frame: this._aerialFrame,
    });
    if (this._aerial && this._aerial.size === ap.size) {
      (this._aerial.texture.image.data as Float32Array).set(ap.data);
      this._aerial.texture.needsUpdate = true;
      this._aerial.data = ap.data;
      ap.texture.dispose();
    } else {
      this._aerial?.texture.dispose();
      this._aerial = ap;
    }
  }

  dispose(): void {
    this._disposed = true;
    this._transmittance?.texture.dispose();
    this._multiScatter?.texture.dispose();
    this._skyView?.texture.dispose();
    this._aerial?.texture.dispose();
    this._transmittance = null;
    this._multiScatter = null;
    this._skyView = null;
    this._aerial = null;
  }
}
